import type { Meeting, MeetingSummary } from "@scoach/types";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { effectiveGeminiKey, ensureGeminiKey } from "../../../../lib/geminiKey.ts";
import { api } from "../../../../lib/http.ts";
import { useAuthStore } from "../../../auth/store.ts";
import { preMeetingApi } from "../../../premeeting/api.ts";
import { getScenarioById } from "../../../simulation/scenarios.ts";
import { useAudioCapture } from "../audio/useAudioCapture.ts";
import { useAudioUploader } from "../audio/useAudioUploader.ts";
import { useMicCapture } from "../audio/useMicCapture.ts";
import { useScreenCapture } from "../audio/useScreenCapture.ts";
import { buildSimulationPrompt } from "../gemini/buildSimulationPrompt.ts";
import { useGeminiLive } from "../gemini/useGeminiLive.ts";
import { useInactivityAutoEnd } from "../hooks/useInactivityAutoEnd.ts";
import { useLiveCustomSummary } from "../hooks/useLiveCustomSummary.ts";
import { useLiveMeeting } from "../hooks/useLiveMeeting.ts";
import { useLiveMeetingStore } from "../store.ts";
import { AppHeader } from "./AppHeader.tsx";
import { CoachColumn } from "./CoachColumn.tsx";
import { ContextRail } from "./ContextRail.tsx";
import { InactivityDialog } from "./InactivityDialog.tsx";
import { ResizableMain } from "./ResizableMain.tsx";
import { TranscriptPanel } from "./TranscriptPanel.tsx";

export interface LiveMeetingScreenProps {
  meetingId: string;
}

export function LiveMeetingScreen({ meetingId }: LiveMeetingScreenProps) {
  useLiveMeeting(meetingId);

  const setListening = useLiveMeetingStore((s) => s.setListening);
  const listening = useLiveMeetingStore((s) => s.listening);
  const muted = useLiveMeetingStore((s) => s.muted);
  const nav = useNavigate();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [parentSummary, setParentSummary] = useState<MeetingSummary | null>(null);

  // Answer the rep's custom questions live from the transcript (on their key),
  // using the per-meeting question set (role / meeting type) when one was chosen.
  useLiveCustomSummary(meeting?.customQuestionKey);

  const isSimulation = meeting?.meetingType === "simulation";
  const user = useAuthStore((s) => s.user);
  const simulationLanguage = useAuthStore((s) => s.simulationLanguage);

  const tabUploader = useAudioUploader({ meetingId, source: "tab" });
  const micUploader = useAudioUploader({ meetingId, source: "mic" });

  const simPrompt = useMemo(() => {
    if (!isSimulation || !meeting) return "";
    const scenario =
      meeting.simulationScenario ??
      (meeting.simulationScenarioId ? (getScenarioById(meeting.simulationScenarioId) ?? null) : null);
    return buildSimulationPrompt({
      clientName: meeting.account.name || "the client",
      contactName: meeting.participants.find((p) => p.side === "client")?.name,
      contactRole: meeting.participants.find((p) => p.side === "client")?.role,
      industry: meeting.account.industry,
      stage: meeting.stage,
      meetingGoal: meeting.goal,
      summary: parentSummary,
      scenario,
      companyName: user?.companyName,
      language: simulationLanguage,
    });
  }, [isSimulation, meeting, parentSummary, simulationLanguage, user?.companyName]);

  const geminiLive = useGeminiLive({
    meetingId,
    systemInstruction: simPrompt,
    enabled: isSimulation,
    language: simulationLanguage,
  });

  const handleTabFrame = useCallback(
    (frame: { pcm: Int16Array }) => {
      tabUploader.push(frame.pcm);
    },
    [tabUploader],
  );
  const handleMicFrame = useCallback(
    (frame: { pcm: Int16Array }) => {
      micUploader.push(frame.pcm);
      if (isSimulation) {
        geminiLive.sendAudio(frame.pcm);
      }
    },
    [micUploader, isSimulation, geminiLive],
  );

  const audio = useAudioCapture({ onFrame: handleTabFrame, paused: muted });
  const mic = useMicCapture({ onFrame: handleMicFrame, paused: muted });
  useScreenCapture({ stream: audio.stream, meetingId });

  const startWithPrompt = useCallback(async () => {
    let key = effectiveGeminiKey();
    if (!key) {
      key = await ensureGeminiKey();
    }
    if (isSimulation) {
      if (key) geminiLive.connect();
      void mic.start();
      return;
    }
    // Live meeting: always capture the rep's mic, and auto-prompt to also capture
    // the other party's audio coming into the laptop (audio-only — we ignore the
    // shared video). The picker also lets the rep pick a tab/window for "share
    // audio". If they dismiss/deny it, we silently continue mic-only.
    void mic.start();
    void audio.start();
  }, [mic, audio, isSimulation, geminiLive, nav]);

  const startScreenShare = useCallback(async () => {
    // The "Share screen" button runs the SAME flow as the header "Start meeting"
    // button when the meeting hasn't started yet — users clicked it expecting it to
    // start the meeting (mic + screen), so it now does exactly that (Gemini-key
    // check, mic capture, and the screen picker). Once capture is already running it
    // just switches/adds the shared tab (re-open the picker) without restarting mic.
    if (!mic.capturing && !audio.capturing) {
      void startWithPrompt();
      return;
    }
    void audio.start();
  }, [mic.capturing, audio.capturing, startWithPrompt, audio]);

  const stopScreenShare = useCallback(() => {
    audio.stop();
  }, [audio]);

  useEffect(() => {
    let cancelled = false;
    preMeetingApi
      .fetchMeeting(meetingId)
      .then((m) => {
        if (cancelled) return;
        setMeeting(m);
        useLiveMeetingStore.getState().setMeetingCustomQuestionKey(m?.customQuestionKey ?? null);
        useLiveMeetingStore.getState().setMeetingRole(m?.role ?? null);
        useLiveMeetingStore.getState().setHintFocus(m?.hintFocus ?? "standard");
        if (m?.notes && m.notes.length > 0) {
          useLiveMeetingStore.getState().setNotes(m.notes);
        }
        // Load parent meeting summary for simulation context
        if (m?.meetingType === "simulation" && m.parentMeetingId) {
          api<{ summary: MeetingSummary }>(`/meetings/${m.parentMeetingId}/summary-data`)
            .then((r) => {
              if (!cancelled) setParentSummary(r.summary);
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        if (!cancelled) setMeeting(null);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  useEffect(() => {
    const live = audio.capturing || mic.capturing;
    setListening(live);
    void api(`/meetings/${meetingId}/state`, {
      method: "PATCH",
      body: { listening: live },
    }).catch(() => {});
  }, [audio.capturing, mic.capturing, setListening, meetingId]);

  useEffect(() => {
    return () => {
      audio.stop();
      mic.stop();
    };
  }, [audio.stop, mic.stop]);

  async function handleEnd() {
    if (isSimulation) {
      geminiLive.disconnect();
    }
    audio.stop();
    mic.stop();
    try {
      await Promise.race([
        Promise.all([tabUploader.flush(), micUploader.flush()]),
        new Promise((r) => setTimeout(r, 3000)),
      ]);
    } catch {}
    void api(`/meetings/${meetingId}/end`, { method: "POST", body: {} }).catch(() => {});
    nav({ to: "/meetings/$id/summary", params: { id: meetingId } });
  }

  // Auto-close a forgotten meeting: if it's capturing but no one speaks for a few
  // minutes, prompt the user; end automatically if they don't respond in time.
  const inactivity = useInactivityAutoEnd(listening, handleEnd);

  if (!meeting) {
    return <div style={{ padding: 32, color: "var(--text-3)" }}>Loading meeting…</div>;
  }

  return (
    <div className="app">
      <AppHeader
        meeting={meeting}
        onToggleListening={startWithPrompt}
        onEnd={handleEnd}
        isSimulation={isSimulation}
        simulationConnected={geminiLive.connected}
        simulationError={geminiLive.error}
      />
      {!isSimulation && mic.error && !muted && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            margin: "8px 16px 0",
            padding: "10px 14px",
            borderRadius: 8,
            background: "var(--gc-red-50, #fce8e6)",
            color: "var(--gc-red-900, #b00020)",
            border: "1px solid var(--gc-red-700, #d93025)",
            fontSize: 13,
          }}
        >
          <span style={{ flex: 1, minWidth: 220 }}>
            🎤 {mic.error} SellSynq is still transcribing the shared meeting audio.
            {/Mac/i.test(navigator.userAgent) && (
              <>
                {" "}
                On macOS, check{" "}
                <strong>System Settings → Privacy &amp; Security → Microphone</strong> and enable
                your browser, then Retry.
              </>
            )}
          </span>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => void mic.start()}
            style={{ flexShrink: 0 }}
          >
            Retry mic
          </button>
        </div>
      )}
      <ResizableMain
        rail={
          <ContextRail
            meeting={meeting}
            isSimulation={isSimulation}
            stream={isSimulation ? null : audio.stream}
            rmsDb={isSimulation ? Number.NEGATIVE_INFINITY : audio.rmsDb}
            error={isSimulation ? null : audio.error}
            onPickSource={isSimulation ? undefined : startScreenShare}
            onStopShare={isSimulation ? undefined : stopScreenShare}
            micCapturing={mic.capturing}
            micError={mic.error}
            listening={listening}
          />
        }
        coach={<CoachColumn meetingId={meetingId} isInternal={!!meeting.account?.isInternal} />}
        transcript={
          <TranscriptPanel
            meetingId={meetingId}
            meetingType={meeting?.meetingType}
            meetingRole={meeting?.role}
            roleKnown={!!meeting}
          />
        }
      />
      {inactivity.prompting && (
        <InactivityDialog
          secondsLeft={inactivity.secondsLeft}
          onStay={inactivity.stay}
          onEnd={inactivity.endNow}
        />
      )}
    </div>
  );
}
